import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import Swal from "sweetalert2";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import {
  deleteCaptionVocabularyTerm,
  fetchCaptionVocabulary,
  saveCaptionVocabularyTerm,
} from "../../../actions/captionsActions";

const CATEGORIES = [
  { value: "tapping_point", label: "Tapping point" },
  { value: "setup_phrase", label: "Setup phrase" },
  { value: "technical", label: "Technical" },
  { value: "brand", label: "Brand" },
  { value: "author_name", label: "Author name" },
  { value: "other", label: "Other" },
];

const CATEGORY_COLOR = {
  tapping_point: "primary",
  setup_phrase: "secondary",
  technical: "info",
  brand: "warning",
  author_name: "success",
  other: "default",
};

const blankTerm = () => ({
  canonical_form: "",
  category: "other",
  known_mishearings: [],
  notes: "",
  is_active: true,
});

const CaptionsVocabularyPage = () => {
  const dispatch = useDispatch();
  const vocabulary = useSelector(s => s.captions.vocabulary);
  const isLoading = vocabulary === null;

  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(blankTerm());
  const [mishearingText, setMishearingText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Per-id in-flight set so rapid clicks on the active Switch don't fire
  // multiple PUTs whose order of resolution determines the final state.
  const [togglingIds, setTogglingIds] = useState(() => new Set());

  useEffect(() => {
    dispatch(fetchCaptionVocabulary());
  }, [dispatch]);

  const rows = useMemo(() => {
    if (!Array.isArray(vocabulary)) return [];
    const q = search.trim().toLowerCase();
    return vocabulary.filter(t => {
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (q && !String(t.canonical_form).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [vocabulary, categoryFilter, search]);

  const openCreate = () => {
    setDraft(blankTerm());
    setMishearingText("");
    setDialogOpen(true);
  };

  const openEdit = term => {
    setDraft({ ...term });
    setMishearingText((term.known_mishearings ?? []).join(", "));
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const onSave = () => {
    // Guard against rapid double-clicks racing the dialog open state.
    if (submitting) return;
    if (!draft.canonical_form.trim()) {
      Swal.fire({ icon: "warning", title: "Canonical form is required.", timer: 1500 });
      return;
    }
    const payload = {
      ...draft,
      canonical_form: draft.canonical_form.trim(),
      known_mishearings: mishearingText
        .split(",")
        .map(s => s.trim())
        .filter(Boolean),
      notes: draft.notes?.trim() || null,
    };
    setSubmitting(true);
    dispatch(saveCaptionVocabularyTerm(payload, () => setDialogOpen(false))).finally(() => setSubmitting(false));
  };

  const onToggleActive = term => {
    // Inline switch toggle — skip the success modal so the row updates silently.
    // Per-id in-flight guard prevents rapid clicks from queuing N PUTs whose
    // resolution order would determine the final state non-deterministically.
    // Errors still surface via the thunk's errorHandler; the `.catch` here
    // just swallows the rethrown rejection so it isn't logged as unhandled.
    // The controlled Switch will snap back to the prior `is_active` value on
    // its next render because the UPSERT only dispatches on success.
    if (togglingIds.has(term.id)) return;
    setTogglingIds(prev => {
      const next = new Set(prev);
      next.add(term.id);
      return next;
    });
    dispatch(saveCaptionVocabularyTerm({ ...term, is_active: !term.is_active }, null, { silent: true }))
      .catch(() => {})
      .finally(() => {
        setTogglingIds(prev => {
          const next = new Set(prev);
          next.delete(term.id);
          return next;
        });
      });
  };

  const onDelete = async term => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: term.is_active ? "Deactivate this term?" : "Permanently delete this term?",
      text: term.is_active
        ? "The term will be removed from prompt + post-processor rotation but kept on file for re-activation."
        : "This row is already inactive. Permanently deleting it removes it from the database — there's no undo.",
      showCancelButton: true,
      confirmButtonText: term.is_active ? "Deactivate" : "Delete forever",
      confirmButtonColor: term.is_active ? undefined : "#d33",
    });
    if (!isConfirmed) return;
    dispatch(deleteCaptionVocabularyTerm(term.id, null, { force: !term.is_active }));
  };

  if (isLoading) {
    return <LoadingBackdrop open />;
  }

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Caption Vocabulary
          </Typography>
          <Button startIcon={<Add />} color="inherit" variant="outlined" onClick={openCreate}>
            Add term
          </Button>
        </Toolbar>
      </AppBar>

      <Toolbar sx={{ backgroundColor: "background.paper", borderBottom: 1, borderColor: "divider", gap: 2, py: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="cat-filter-label">Category</InputLabel>
          <Select
            labelId="cat-filter-label"
            value={categoryFilter}
            label="Category"
            onChange={e => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All categories</MenuItem>
            {CATEGORIES.map(c => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search canonical form"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 280, flexGrow: 1, maxWidth: 480 }}
        />
        <Typography variant="caption" color="text.secondary">
          {rows.length} term{rows.length === 1 ? "" : "s"} shown
        </Typography>
      </Toolbar>

      <Box sx={{ py: 2 }}>
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <strong>Canonical form</strong>
                </TableCell>
                <TableCell>
                  <strong>Category</strong>
                </TableCell>
                <TableCell>
                  <strong>Mishearings</strong>
                </TableCell>
                <TableCell>
                  <strong>Notes</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Active</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No terms match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(term => (
                  <TableRow key={term.id} hover sx={{ opacity: term.is_active ? 1 : 0.55 }}>
                    <TableCell>
                      {term.canonical_form}
                      {!term.is_active && (
                        // Belt-and-suspenders for high-contrast/non-sighted users
                        // — the 0.55 opacity alone is color-only signaling.
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1, fontStyle: "italic" }}
                        >
                          (inactive)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={term.category.replace(/_/g, " ")}
                        color={CATEGORY_COLOR[term.category] || "default"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {(term.known_mishearings ?? []).length === 0 ? (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {term.known_mishearings.slice(0, 4).map(m => (
                            <Chip key={m} label={m} size="small" />
                          ))}
                          {term.known_mishearings.length > 4 && (
                            <Chip label={`+${term.known_mishearings.length - 4}`} size="small" variant="outlined" />
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={term.notes ?? ""}>
                        {term.notes || ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={!!term.is_active}
                        onChange={() => onToggleActive(term)}
                        size="small"
                        disabled={togglingIds.has(term.id)}
                        inputProps={{ "aria-label": `Toggle active for ${term.canonical_form}` }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(term)}
                          aria-label={`Edit ${term.canonical_form}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={term.is_active ? "Deactivate" : "Delete permanently"}>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(term)}
                          color={term.is_active ? "default" : "error"}
                          aria-label={`${term.is_active ? "Deactivate" : "Delete"} ${term.canonical_form}`}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{draft.id ? "Edit term" : "Add term"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Canonical form"
              value={draft.canonical_form}
              onChange={e => setDraft({ ...draft, canonical_form: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel id="cat-label">Category</InputLabel>
              <Select
                labelId="cat-label"
                value={draft.category}
                label="Category"
                onChange={e => setDraft({ ...draft, category: e.target.value })}
              >
                {CATEGORIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Known mishearings"
              helperText="Comma-separated. The transcriber sees these and the post-processor swaps them for the canonical form."
              value={mishearingText}
              onChange={e => setMishearingText(e.target.value)}
              fullWidth
            />
            <TextField
              label="Notes"
              value={draft.notes ?? ""}
              onChange={e => setDraft({ ...draft, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!draft.is_active}
                  onChange={e => setDraft({ ...draft, is_active: e.target.checked })}
                />
              }
              label="Active (included in prompt + post-processor rotation)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onSave} variant="contained" disabled={submitting}>
            {draft.id ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContentContainer>
  );
};

export default CaptionsVocabularyPage;
